/**
 * Book — the main orchestrator for EPUB parsing.
 *
 * Replaces epubjs/src/book.js's parsing pipeline while delegating
 * rendering (renderTo, Rendition, EpubCFI) to epub.js until Phase 2-3.
 */
import { Archive } from "./archive";
import { parseContainer } from "./container";
import {
  parsePackaging,
  type PackagingResult,
  type Metadata,
  type ManifestItem,
} from "./packaging";
import { parseNavigation, type NavigationResult } from "./navigation";
import { Spine } from "./spine";
import { Resources } from "./resources";
import { Locations } from "../locations";

const CONTAINER_PATH = "META-INF/container.xml";
const EPUBJS_VERSION = "0.3"; // match epub.js key format

export interface BookLoaded {
  metadata: Promise<Metadata>;
  navigation: Promise<NavigationResult>;
  resources: Promise<Resources>;
  cover: Promise<string>;
  manifest: Promise<Record<string, ManifestItem>>;
  spine: Promise<Spine>;
}

/**
 * Deferred helper — creates a promise + its resolve/reject handles.
 */
function deferred<T>(): {
  promise: Promise<T>;
  resolve: (val: T) => void;
  reject: (err: unknown) => void;
} {
  let resolve!: (val: T) => void;
  let reject!: (err: unknown) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

export class ParsedBook {
  destroyed = false;
  readonly archive = new Archive();
  readonly spine = new Spine();
  readonly locations = new Locations();

  packaging!: PackagingResult;
  resources!: Resources;
  navigation!: NavigationResult;

  /** Path directory inside the EPUB (e.g. "OPS/" or "OEBPS/") */
  private directory = "";
  /** Path to the OPF file */
  private packagePath = "";

  // Public promise API matching epub.js
  private _readyDeferred = deferred<void>();
  private _replacementsDeferred = deferred<void>();
  private _metadataDeferred = deferred<Metadata>();
  private _navigationDeferred = deferred<NavigationResult>();
  private _resourcesDeferred = deferred<Resources>();
  private _coverDeferred = deferred<string>();
  private _manifestDeferred = deferred<Record<string, ManifestItem>>();
  private _spineDeferred = deferred<Spine>();

  readonly ready: Promise<void> = this._readyDeferred.promise;
  /** Resolves after all resource blob URLs are created (after ready). */
  readonly replacementsReady: Promise<void> =
    this._replacementsDeferred.promise;
  readonly loaded: BookLoaded = {
    metadata: this._metadataDeferred.promise,
    navigation: this._navigationDeferred.promise,
    resources: this._resourcesDeferred.promise,
    cover: this._coverDeferred.promise,
    manifest: this._manifestDeferred.promise,
    spine: this._spineDeferred.promise,
  };

  private coverPath = "";
  private identifier = "";

  constructor(data?: ArrayBuffer | Uint8Array) {
    if (data) this.open(data);
  }

  async open(data: ArrayBuffer | Uint8Array): Promise<void> {
    try {
      await this.archive.open(data);

      // 1. Parse container.xml
      const containerDoc = (await this.archive.request(
        CONTAINER_PATH,
        "xml",
      )) as Document;
      const container = parseContainer(containerDoc);
      this.packagePath = container.packagePath;
      this.directory = container.directory;

      // 2. Parse OPF
      const opfDoc = (await this.archive.request(
        this.packagePath,
        "xml",
      )) as Document;
      this.packaging = parsePackaging(opfDoc);
      this.identifier =
        this.packaging.uniqueIdentifier || this.packaging.metadata.identifier;

      // 3. Build spine — also enriches this.packaging.spine entries with href/cfiBase
      const resolver = (href: string) => this.resolve(href);
      this.spine.unpack(this.packaging, resolver);

      // Wire up locations with spine + archive loader
      this.locations.setup(
        this.spine,
        // Spine URLs are already archive-relative. Parse every item as a
        // document (including SVG), with HTML parsing for legacy .html/.htm.
        (url: string) =>
          this.archive.request(url, /\.html?$/i.test(url) ? "html" : "xhtml") as Promise<Document>,
      );

      // Enrich packaging.spine entries with href/url/cfiBase to match epub.js mutation
      for (const section of this.spine.items) {
        const entry = this.packaging.spine[section.index] as any;
        entry.href = section.href;
        entry.url = section.url;
        entry.cfiBase = section.cfiBase;
      }

      // 4. Build resources
      this.resources = new Resources(this.packaging.manifest, {
        archive: this.archive,
        resolver,
        replacements: "blobUrl",
      });
      console.debug("[epub:book] resources created", {
        assetCount: this.resources.urls.length,
        cssCount: this.resources.cssUrls.length,
        cssUrls: this.resources.cssUrls,
      });

      // 5. Load navigation
      this.navigation = await this.loadNavigation();

      // 6. Cover
      this.coverPath = this.packaging.coverPath
        ? this.resolve(this.packaging.coverPath)
        : "";

      // Resolve all loaded promises
      this._metadataDeferred.resolve(this.packaging.metadata);
      this._manifestDeferred.resolve(this.packaging.manifest);
      this._spineDeferred.resolve(this.spine);
      this._navigationDeferred.resolve(this.navigation);
      this._resourcesDeferred.resolve(this.resources);
      this._coverDeferred.resolve(this.coverPath);

      // Resolve ready BEFORE replacements — matches epub.js behavior.
      // This lets the rendition start displaying content immediately while
      // resource blob URLs are still being created in the background.
      this._readyDeferred.resolve();
      console.debug("[epub:book] ready resolved (replacements pending)");

      // 7. Create blob URLs for resources (runs after ready so display isn't blocked)
      await this.resources.replacements();
      console.debug("[epub:book] replacements() done", {
        replacementCount: this.resources.replacementUrls.filter(Boolean).length,
        total: this.resources.replacementUrls.length,
      });
      await this.resources.replaceCss();
      console.debug("[epub:book] replaceCss() done", {
        cssUrls: this.resources.cssUrls,
        cssReplacements: this.resources.cssUrls.map((href) => {
          const idx = this.resources.urls.indexOf(href);
          const replacement =
            idx > -1 ? this.resources.replacementUrls[idx] : "NOT_FOUND";
          return {
            href,
            idx,
            hasReplacement: !!replacement,
            prefix: replacement?.slice(0, 40),
          };
        }),
      });
      this._replacementsDeferred.resolve();
      console.debug("[epub:book] all resource processing complete");
    } catch (err) {
      // Reject ALL deferreds so no consumer hangs on an unresolved promise
      const rejectAll = (reason: unknown) => {
        this._readyDeferred.reject(reason);
        this._replacementsDeferred.reject(reason);
        this._metadataDeferred.reject(reason);
        this._navigationDeferred.reject(reason);
        this._resourcesDeferred.reject(reason);
        this._coverDeferred.reject(reason);
        this._manifestDeferred.reject(reason);
        this._spineDeferred.reject(reason);
      };
      rejectAll(err);
      throw err;
    }
  }

  private async loadNavigation(): Promise<NavigationResult> {
    const { navPath, ncxPath } = this.packaging;
    if (navPath) {
      const resolved = this.resolve(navPath);
      const doc = (await this.archive.request(resolved, "xhtml")) as Document;
      return parseNavigation(doc, false);
    }
    if (ncxPath) {
      const resolved = this.resolve(ncxPath);
      const doc = (await this.archive.request(resolved, "xml")) as Document;
      return parseNavigation(doc, true);
    }
    return {
      toc: [],
      tocByHref: {},
      tocById: {},
      landmarks: [],
      landmarksByType: {},
    };
  }

  /**
   * Resolve a relative href against the EPUB's content directory.
   */
  resolve(href: string): string {
    if (href.includes("://")) return href;
    // Prefix with directory
    let resolved = href;
    if (this.directory && !href.startsWith(this.directory)) {
      resolved = this.directory + href;
    }
    // Normalize . and .. path components (JSZip doesn't handle them)
    if (
      resolved.includes("/./") ||
      resolved.includes("/../") ||
      resolved.startsWith("../")
    ) {
      const parts = resolved.split("/");
      const normalized: string[] = [];
      for (const part of parts) {
        if (part === "..") {
          normalized.pop();
        } else if (part !== ".") {
          normalized.push(part);
        }
      }
      return normalized.join("/");
    }
    return resolved;
  }

  /**
   * Load a file from the archive. Returns parsed content (typically a Document).
   */
  async load(href: string): Promise<Document | string | unknown> {
    const resolved = this.resolve(href);
    return this.archive.request(resolved);
  }

  /**
   * Get a unique key for this book (for caching).
   */
  key(identifier?: string): string {
    return `epubjs:${EPUBJS_VERSION}:${identifier ?? this.identifier ?? ""}`;
  }

  /**
   * Get the cover image URL (blob URL or data URI).
   */
  async coverUrl(): Promise<string | null> {
    await this.loaded.cover;
    if (!this.coverPath) return null;
    return this.archive.createUrl(this.coverPath);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.spine.destroy();
    this.locations.destroy();
    this.resources?.destroy();
    this.archive.destroy();
  }
}

/**
 * Factory function matching epub.js's `Epub(data)` signature.
 */
export function createBook(data: ArrayBuffer | Uint8Array): ParsedBook {
  return new ParsedBook(data);
}
